/**
 * FieldIndicatorWrapper
 *
 * Purpose: Shared wrapper for Maestro field indicator UI. Renders the visual
 *          Tooltip + border-color Box (ORCH-251) and a visually-hidden description
 *          element that associates indicator status text with the field input via
 *          aria-describedby (ORCH-237). The hidden element is aria-live so that
 *          screen readers announce state changes when Maestro applies values live.
 * Architecture: Extracted from TemplateField and ExtraFields to ensure both
 *               rendering paths stay in sync — one implementation, no drift.
 * Dependencies: MUI (Box, Tooltip), ./FieldIndicatorIcon (FieldIndicatorState, INDICATOR_BORDER)
 */

import React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import type { FieldIndicatorState } from './FieldIndicatorIcon';
import { INDICATOR_BORDER } from './FieldIndicatorIcon';

/** Returns the stable aria-describedby target ID for a field's indicator description. */
export function indicatorDescriptorId(fieldId: string): string {
  return `${fieldId}-indicator-desc`;
}

const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
};

interface FieldIndicatorWrapperProps {
  fieldId: string;
  indicator: FieldIndicatorState;
  children: React.ReactNode;
}

/**
 * FieldIndicatorWrapper — visual + accessible indicator shell for one field
 *
 * Purpose: Wraps a field renderer with the Maestro border-color indicator and
 *          wires up screen-reader accessibility for the indicator status text.
 * Logic: 1) Renders a visually-hidden span (aria-live="polite") containing
 *           indicator.tooltipText so screen readers can read it via aria-describedby.
 *        2) Wraps children in a MUI Tooltip (hover-only, for mouse users).
 *        3) Applies border-color sx via data-indicator Box (ORCH-251).
 * Input: fieldId (used to generate stable aria IDs), indicator (state + tooltip text), children
 * Output: Fragment containing the hidden description + Tooltip-wrapped indicator Box
 */
const FieldIndicatorWrapper: React.FC<FieldIndicatorWrapperProps> = ({
  fieldId,
  indicator,
  children,
}) => {
  const borderColor = indicator.state ? INDICATOR_BORDER[indicator.state] : undefined;
  const descId = indicatorDescriptorId(fieldId);

  return (
    <>
      <Box component="span" id={descId} aria-live="polite" style={VISUALLY_HIDDEN}>
        {indicator.tooltipText ?? ''}
      </Box>
      <Tooltip title={indicator.tooltipText ?? ''} arrow>
        <Box
          data-indicator={indicator.state ?? undefined}
          sx={
            borderColor
              ? {
                  '& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root': {
                    '& fieldset': { borderColor },
                    '&:hover fieldset': { borderColor },
                  },
                }
              : undefined
          }
        >
          {children}
        </Box>
      </Tooltip>
    </>
  );
};

export default FieldIndicatorWrapper;
